'use client'
import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { KNOWLEDGE_SOURCE_PROVIDERS } from '@/lib/knowledge-sources/providers'
import { useKnowledgeSourcesQuery } from '@/hooks/queries/knowledge-sources'
import { useKnowledgeSourceMutation } from '@/hooks/mutations/knowledge-sources'
import { KnowledgeSourceConnectDialog } from './connect-dialog'
import { KnowledgeSourceSelectedFiles } from './selected-files'
import { knowledgeSourceStatusLabel } from './status-label'
import { KnowledgeSourceProviderIcon } from './provider-icon'

/** Existing setup flows import in place, then explicitly continue with completed document IDs. */
export function KnowledgeSourceImportForm({ isDefault = false, onImported }: {
  isDefault?: boolean; onImported(documentIds: string[]): void | Promise<void>;
}) {
  const t = useTranslations('KnowledgeSources')
  const wizard = useTranslations('AgentCreation')
  const query = useKnowledgeSourcesQuery()
  const mutation = useKnowledgeSourceMutation()
  const [folderId, setFolderId] = useState<string>()
  const previousSource = query.data?.sources
    .filter(source => isDefault && source.isDefault && source.status === 'active' && source.items.length > 0)
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0]
  const activeFolderId = folderId ?? previousSource?.id
  const source = query.data?.sources.find(source => source.id === activeFolderId)
  const pending = !source || source.items.some(item => item.status === 'pending')
  const failed = source?.items.some(item => item.status !== 'pending' && item.status !== 'synced')
  const documentIds = source?.items.filter(item => item.status === 'synced' && item.documentId).map(item => item.documentId!) ?? []
  const form = useFormik({ initialValues: {}, onSubmit: async (_, helpers) => {
    try { await onImported(documentIds) }
    catch (error) { helpers.setStatus(error instanceof Error ? error.message : wizard('failed')) }
  } })
  if (query.isLoading) return <p role="status" className="flex items-center gap-2 p-4"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /><span>{t('loading')}</span></p>
  if (!activeFolderId) return <Card>
    <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><KnowledgeSourceProviderIcon provider={KNOWLEDGE_SOURCE_PROVIDERS[0].id} /><span>{KNOWLEDGE_SOURCE_PROVIDERS[0].name}</span></CardTitle>
      <CardDescription><span>{t('guidance')}</span></CardDescription></CardHeader>
    <KnowledgeSourceConnectDialog open presentation="inline" isDefault={isDefault} onOpenChange={() => {}} onImported={({ folderId }) => setFolderId(folderId)} />
  </Card>
  return <Card><CardContent className="space-y-4 p-4 sm:p-6">
    <FormikProvider value={form}><form onSubmit={form.handleSubmit} className="space-y-4">
      {pending && !query.isError && <p role="status" className="flex items-center gap-2 text-sm"><Loader2 aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin" /><span>{wizard('importing')}</span></p>}
      {query.isError && <p role="alert" className="text-sm text-destructive"><span>{wizard('failed')}</span></p>}
      {source && <KnowledgeSourceSelectedFiles files={source.items.map(item => ({ id: item.id, name: item.name, description: knowledgeSourceStatusLabel(item.status, t) }))} />}
      {failed && <p role="alert" className="text-sm text-destructive"><span>{wizard('importFailed')}</span></p>}
      {form.status && <p role="alert" className="text-sm text-destructive"><span key={String(form.status)}>{String(form.status)}</span></p>}
      <div className="flex flex-wrap justify-end gap-2">
        {query.isError && <Button type="button" variant="outline" onClick={() => void query.refetch()}><span>{wizard('retry')}</span></Button>}
        {failed && <Button type="button" variant="outline" disabled={mutation.isPending} onClick={() => void mutation.mutateAsync({ kind: 'sync', folderId: activeFolderId }).catch(() => {})}><span>{t('syncNow')}</span></Button>}
        <Button type="submit" disabled={pending || documentIds.length === 0 || form.isSubmitting || mutation.isPending} aria-busy={form.isSubmitting}>
          {form.isSubmitting && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}<span>{wizard('next')}</span>
        </Button>
      </div>
    </form></FormikProvider>
  </CardContent></Card>
}
